import {
  Bed,
  BedKey,
  BedKeySchema,
  DEFAULT_LOCALE,
  DELETION_FLAG,
  ERROR_CODE,
  UniversalError,
  UserKey,
} from "@packages/core";
import { useForm } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { LuChevronsLeft, LuChevronsRight } from "react-icons/lu";
import { from, lastValueFrom, mergeMap } from "rxjs";
import * as v from "valibot";

import {
  ButtonStyle,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PaperStyle,
  TableStyle,
  TypographyStyle,
} from "#components/common";
import { Breadcrumbs } from "#components/elements";
import { PageName, showError } from "#components/utilities";
import {
  $alert,
  $confirm,
  modifyBed,
  registerGroup,
  removeGroupInfiniteQueryCache,
  resetBedInfiniteQueries,
  useBedInfiniteQuery,
  useFacilityQuery,
  useGetErrorMessage,
  useResident,
  useSm,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/groups/registration")(
  { component: Component },
);

const classes = TableStyle.raw({ foot: false });

function Component() {
  const { facilityKey } = Route.useParams();

  const facilityQuery = useFacilityQuery(facilityKey);
  const { data: facility } = facilityQuery;
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const sm = useSm();

  const beds = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .sort(function (a, b) {
            return (
              a.roomName.localeCompare(b.roomName) ||
              a.roomPronunciation.localeCompare(b.roomPronunciation) ||
              a.bedName.localeCompare(b.bedName) ||
              a.bedPronunciation.localeCompare(b.bedPronunciation) ||
              a.bedKey.localeCompare(b.bedKey)
            );
          })
          .reduce(function (beds, bed) {
            return beds.set(bed.bedKey, bed);
          }, new Map<BedKey, Bed>()) ?? new Map<BedKey, Bed>()
      );
    },
    [bedInfiniteQuery.data],
  );

  const formSchema = useMemo(function () {
    return v.object({
      bedKeys: v.pipe(
        v.array(v.message(BedKeySchema(), ERROR_CODE.UNEXPECTED), ERROR_CODE.UNEXPECTED),
        v.check(function (elements) {
          // 重複が無い事を保証する。
          return new Set(elements).size === elements.length;
        }, ERROR_CODE.UNEXPECTED),
      ),
      name: v.pipe(
        v.string(ERROR_CODE.INPUT_REQUIRED),
        v.minLength(1, ERROR_CODE.INPUT_REQUIRED),
        v.maxGraphemes(128, ERROR_CODE.TOO_MANY_CHARACTERS),
      ),
    });
  }, []);
  const defaultValues = useMemo(function (): v.InferInput<typeof formSchema> {
    return { bedKeys: [], name: "" };
  }, []);

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      let output;
      try {
        output = v.parse(formSchema, input);
      } catch (error) {
        console.error(error);
        showError(new UniversalError(ERROR_CODE.INVALID_FORM));
        return;
      }

      console.debug("フォームが送信されました。", output);

      if (
        output.bedKeys.length < 10 &&
        !(await $confirm({
          color: "warning",
          content: translate("%GroupForm.14%"),
          no: translate("%Back.0%"),
          title: translate("%GroupForm.13%"),
          yes: translate("%GroupForm.15%"),
        }))
      ) {
        return;
      }

      let group;
      try {
        const response = await registerGroup({ ...output, facilityKey });
        group = response.group;
      } catch (error) {
        showError(error);
        return;
      }

      if (output.bedKeys.length > 0) {
        await lastValueFrom(
          from(output.bedKeys).pipe(
            mergeMap(async function (bedKey) {
              try {
                await modifyBed({ bedKey, groupKey: group.groupKey });
              } catch (error) {
                showError(error);
              }
            }, 8),
          ),
        );
      }

      await $alert({
        color: "success",
        content: translate("%Register.2%", { $: { name: group.name } }),
        title: translate("%Register.0%"),
      });

      resetBedInfiniteQueries();
      removeGroupInfiniteQueryCache();

      await navigate({
        ignoreBlocker: true,
        params: { facilityKey },
        to: "/facilities/$facilityKey/groups",
      });
    },
    async onSubmitInvalid() {
      await $alert({
        color: "error",
        content: translate("%Form.3%"),
        title: translate("%Form.2%"),
      });
    },
    validators: { onSubmit: formSchema },
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: translate("%Form.1%"),
          no: translate("%Stay.0%"),
          title: translate("%Form.0%"),
          yes: translate("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <form
      className={css({
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        padding: { smDown: "12px 16px", sm: "16px 24px" },
      })}
      data-main
      data-maximized={sm ? true : undefined}
      onSubmit={function (event) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <PageName>
        {translate("%Add.3%", { $: { name: translate("%Group.0%") } }) +
          " - " +
          (facility ? facility.name : translate("%Loading.0%"))}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "8px",
          justifyContent: { smDown: "end", sm: "space-between" },
        })}
      >
        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={TypographyStyle({
              color: "muted",
              fontSize: "14px",
              lineHeight: 1,
              link: true,
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/groups"
          >
            {translate("%GroupManagement.0%")}
          </Link>
          <p className={TypographyStyle({ color: "default", fontSize: "14px", lineHeight: 1 })}>
            {translate("%Add.1%")}
          </p>
        </Breadcrumbs>

        <div className={css({ display: "flex", gap: "8px", minWidth: 0 })}>
          <Link
            className={ButtonStyle({
              appearance: "ghost",
              borderRadius: "4px",
              color: "ghost",
              fontSize: "14px",
              gap: "8px",
              minWidth: 0,
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/groups"
          >
            <span className={TypographyStyle({ ellipsis: true })}>{translate("%Cancel.0%")}</span>
          </Link>
          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    fontSize: "14px",
                    gap: "8px",
                    minWidth: 0,
                    padding: "10px 16px",
                  })}
                  disabled={submitting}
                  type="submit"
                >
                  <span className={TypographyStyle({ ellipsis: true })}>
                    {translate("%Save.0%")}
                  </span>
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </div>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          marginTop: { smDown: "12px", sm: "6px" },
          padding: { smDown: "16px", sm: "16px 24px" },
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: "14 / 9",
          })}
        >
          {translate("%GroupForm.0%")}
        </h2>

        <form.Field name="name">
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "16px", width: "stretch", maxWidth: { sm: "320px" } })}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%GroupForm.1%")}
              >
                <FormFieldInput
                  className={FormFieldInputStyle({ width: "stretch" })}
                  id={field.name}
                  onBlur={function () {
                    field.handleBlur();
                  }}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </form.Field>
      </div>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          display: "grid",
          flexGrow: 1,
          minHeight: 0,
          gap: "16px",
          marginTop: "6px",
          gridTemplateColumns: { smDown: "1fr", sm: "repeat(2, 1fr)" },
          padding: { smDown: "16px", sm: "16px 24px" },
        })}
      >
        <div
          className={css({
            backgroundColor: "primary-foreground",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            height: { smDown: "400px" },
            minHeight: 0,
            minWidth: 0,
            padding: "16px",
          })}
        >
          <h2
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "14 / 9",
            })}
          >
            {translate("%GroupForm.2%")}
          </h2>

          <p
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "14px",
              lineHeight: "10 / 7",
              marginTop: "16px",
            })}
          >
            {translate("%GroupForm.3%")}
          </p>

          <div className={css({ flexGrow: 1, marginTop: "16px", minWidth: 0, overflowX: "auto" })}>
            <div
              className={css(
                classes.root,
                TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                { height: "stretch" },
              )}
            >
              <div className={css(classes.head)}>
                <div
                  className={css(classes.headRow, {
                    gridTemplateColumns: "repeat(3, minmax(123px, 1fr)) 0.75fr",
                  })}
                >
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Room.0%")}
                  </div>
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Bed.0%")}
                  </div>
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Resident.0%")}
                  </div>
                  <div />
                </div>
              </div>

              <div className={css(classes.body)}>
                <form.Subscribe
                  selector={function (state) {
                    return state.values.bedKeys;
                  }}
                >
                  {function (bedKeys) {
                    return Array.from(beds.values())
                      .filter(function (bed) {
                        return bed.groupKey === facility?.groupKey && !bedKeys.includes(bed.bedKey);
                      })
                      .map(function (bed) {
                        return (
                          <div
                            className={css(classes.bodyRow, {
                              gridTemplateColumns: "repeat(3, minmax(123px, 1fr)) 0.75fr",
                            })}
                            key={bed.bedKey}
                          >
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed.roomName}
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed.bedName}
                            </div>
                            <ResidentNameCell userKey={bed.userKey} />
                            <div className={css({ textAlign: "right" })}>
                              <button
                                className={ButtonStyle({
                                  appearance: "outlined",
                                  borderRadius: "4px",
                                  color: "outlined",
                                  ellipsis: true,
                                  flexGrow: 1,
                                  fontSize: "14px",
                                  gap: "8px",
                                  padding: "12px",
                                })}
                                onClick={function () {
                                  form.setFieldValue("bedKeys", function (current) {
                                    return Array.from(new Set(current).add(bed.bedKey)).sort(
                                      function (bedKeyA, bedKeyB) {
                                        const a = beds.get(bedKeyA as BedKey);
                                        const b = beds.get(bedKeyB as BedKey);
                                        return (
                                          (a && b
                                            ? a.roomName.localeCompare(b.roomName) ||
                                              a.roomPronunciation.localeCompare(
                                                b.roomPronunciation,
                                              ) ||
                                              a.bedName.localeCompare(b.bedName) ||
                                              a.bedPronunciation.localeCompare(b.bedPronunciation)
                                            : 0) || bedKeyA.localeCompare(bedKeyB)
                                        );
                                      },
                                    );
                                  });
                                }}
                              >
                                <span className={TypographyStyle({ ellipsis: true })}>
                                  {translate("%Add.1%")}
                                </span>
                                <LuChevronsRight className={css({ fontSize: "16px" })} />
                              </button>
                            </div>
                          </div>
                        );
                      });
                  }}
                </form.Subscribe>
              </div>
            </div>
          </div>
        </div>

        <div
          className={css({
            backgroundColor: "primary-foreground",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            height: { smDown: "400px" },
            minHeight: 0,
            minWidth: 0,
            padding: "16px",
          })}
        >
          <form.Subscribe
            selector={function (state) {
              return state.values.bedKeys.length;
            }}
          >
            {function (numberOfBeds) {
              return (
                <h2
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: "14 / 9",
                  })}
                >
                  {translate("%GroupForm.4%", {
                    $: { numberOfBeds: numberOfBeds.toLocaleString(DEFAULT_LOCALE) },
                  })}
                </h2>
              );
            }}
          </form.Subscribe>

          <form.Subscribe
            selector={function (state) {
              return state.values.bedKeys;
            }}
          >
            {function (bedKeys) {
              return bedKeys.length > 0 ? (
                <div
                  className={css({
                    flexGrow: 1,
                    marginTop: "16px",
                    minWidth: 0,
                    overflowX: "auto",
                  })}
                >
                  <div
                    className={css(
                      classes.root,
                      TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                      { height: "stretch" },
                    )}
                  >
                    <div className={css(classes.head)}>
                      <div
                        className={css(classes.headRow, {
                          gridTemplateColumns: "0.75fr repeat(3, minmax(123px, 1fr))",
                        })}
                      >
                        <div />
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Room.0%")}
                        </div>
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Bed.0%")}
                        </div>
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Resident.0%")}
                        </div>
                      </div>
                    </div>

                    <div className={css(classes.body)}>
                      {bedKeys.map(function (bedKey, index) {
                        const bed = beds.get(bedKey as BedKey);

                        return (
                          <div
                            className={css(classes.bodyRow, {
                              gridTemplateColumns: "0.75fr repeat(3, minmax(123px, 1fr))",
                            })}
                            key={bed?.bedKey ?? index}
                          >
                            <div>
                              <button
                                className={ButtonStyle({
                                  appearance: "outlined",
                                  borderRadius: "4px",
                                  color: "outlined",
                                  ellipsis: true,
                                  flexGrow: 1,
                                  fontSize: "14px",
                                  gap: "8px",
                                  padding: "12px",
                                })}
                                onClick={function () {
                                  form.setFieldValue("bedKeys", function (current) {
                                    return current.filter(function (bk) {
                                      return bk !== bedKey;
                                    });
                                  });
                                }}
                              >
                                <LuChevronsLeft className={css({ fontSize: "16px" })} />
                                <span className={TypographyStyle({ ellipsis: true })}>
                                  {translate("%Release.0%")}
                                </span>
                              </button>
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed?.roomName}
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed?.bedName}
                            </div>
                            <ResidentNameCell userKey={bed?.userKey} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={css({
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    justifyContent: "center",
                  })}
                >
                  <p
                    className={TypographyStyle({
                      ellipsis: true,
                      fontSize: "14px",
                      lineHeight: "10 / 7",
                    })}
                  >
                    {translate("%GroupForm.5%")}
                  </p>
                </div>
              );
            }}
          </form.Subscribe>
        </div>
      </div>
    </form>
  );
}

function ResidentNameCell({ userKey }: { userKey?: UserKey | null }) {
  const resident = useResident(userKey, { virtualized: true });
  return (
    <div className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: "10 / 7" })}>
      {resident?.name}
    </div>
  );
}
